import {
  CHECKOUT_ROOT_PATH,
  OFFER_CHECKOUT_PATH,
  RESERVATION_EDIT_PATH,
} from 'webReservations/paths';
import { useGuestLandingPaths } from 'webReservations/hooks/useGuestLandingPaths';
import {
  useMatch,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

/**
 * Returns a function that navigates to the appropriate checkout page based on
 * whether the user is booking a new reservation, changing an existing
 * reservation, or checking out with an offer.
 */
export const useNavigateToCheckout = ({ isOffer }: { isOffer: boolean }) => {
  const navigate = useNavigate();
  const { locationSlug, nameSlug } = useParams();
  const [searchParams] = useSearchParams();
  const { legacyRestaurantBasePath, legacyReservationDetailsPath } =
    useGuestLandingPaths();

  const isChangingReservation = useMatch(
    `${legacyReservationDetailsPath}/${RESERVATION_EDIT_PATH}`,
  );

  const referralCode = searchParams.get('referral-code');
  const currentRestaurantPath = legacyRestaurantBasePath({
    locationSlug: locationSlug as string,
    nameSlug: nameSlug as string,
  });

  // If the guest is booking a new reservation, use an absolute path to navigate
  // to checkout. This ensures we support checkout from the restaurant root page
  // as well as the "floor plan slug" page.
  const bookNewReservationCheckoutPath = referralCode
    ? `${currentRestaurantPath}/${CHECKOUT_ROOT_PATH}?referral-code=${searchParams.get('referral-code')}`
    : `${currentRestaurantPath}/${CHECKOUT_ROOT_PATH}`;

  const offerCheckoutPath = `${currentRestaurantPath}/${OFFER_CHECKOUT_PATH}`;

  const checkoutPath = isOffer
    ? offerCheckoutPath
    : isChangingReservation
      ? // If the guest is changing an existing reservation, use a relative path to
        // navigate to checkout within the edit reservation flow.
        CHECKOUT_ROOT_PATH
      : bookNewReservationCheckoutPath;

  return { navigateToCheckout: () => navigate(checkoutPath) };
};
